import { z } from 'zod';
import { validateExternalPromotionalAdUrl, validatePromotionalAdImageFile } from '@dukanti/shared';

const BIDI_OVERRIDE_RE = /[\u202A-\u202E\u2066-\u2069]/;

export interface PromotionalAdFormSchemaOptions {
  readonly requireImage: boolean;
  readonly storeSlug: string;
}

function normalizeOptions(
  options: PromotionalAdFormSchemaOptions | string,
): PromotionalAdFormSchemaOptions {
  if (typeof options === 'string') {
    return { requireImage: false, storeSlug: options };
  }
  return options;
}

export function createPromotionalAdFormSchema(options: PromotionalAdFormSchemaOptions | string) {
  const { requireImage } = normalizeOptions(options);

  return z.object({
    title: z
      .string()
      .trim()
      .max(80, { message: 'admin.promotionalAds.errors.titleTooLong' })
      .refine((value) => !BIDI_OVERRIDE_RE.test(value), {
        message: 'admin.promotionalAds.errors.titleInvalid',
      }),
    alt_text: z
      .string()
      .trim()
      .max(160, { message: 'admin.promotionalAds.errors.altTooLong' })
      .refine((value) => !BIDI_OVERRIDE_RE.test(value), {
        message: 'admin.promotionalAds.errors.altInvalid',
      }),
    destination: z.discriminatedUnion('type', [
      z.object({ type: z.literal('none') }),
      z.object({ type: z.literal('sales') }),
      z.object({
        type: z.literal('category'),
        categoryId: z.string().trim().min(1, {
          message: 'admin.promotionalAds.errors.categoryRequired',
        }),
      }),
      z.object({
        type: z.literal('product'),
        productId: z.string().trim().min(1, {
          message: 'admin.promotionalAds.errors.productRequired',
        }),
      }),
      z.object({
        type: z.literal('external'),
        url: z.string().refine((value) => validateExternalPromotionalAdUrl(value) !== null, {
          message: 'admin.promotionalAds.errors.externalHttpsRequired',
        }),
      }),
    ]),
    is_active: z.boolean(),
    display_order: z.number().int().nonnegative().optional(),
    image: z
      .custom<File | null | undefined>(
        (value) => value === null || value === undefined || value instanceof File,
        { message: 'admin.promotionalAds.errors.mediaInvalidType' },
      )
      .superRefine(async (file, ctx) => {
        if (!file) {
          if (requireImage) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'admin.promotionalAds.errors.mediaRequired',
            });
          }
          return;
        }

        const verdict = await validatePromotionalAdImageFile(file);
        if (verdict === 'imageRequired') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.promotionalAds.errors.mediaRequired',
          });
        } else if (verdict === 'imageTooLarge') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.promotionalAds.errors.mediaTooLarge',
          });
        } else if (verdict === 'imageInvalidType') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.promotionalAds.errors.mediaInvalidType',
          });
        } else if (verdict === 'imageDimensionsInvalid') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.promotionalAds.errors.mediaDimensionsInvalid',
          });
        }
      })
      .optional(),
  });
}

export type PromotionalAdFormValues = z.infer<ReturnType<typeof createPromotionalAdFormSchema>>;
